import React, { useEffect, useState } from 'react';
import {
  fetchVrzOnboardingLink,
  submitVrzOnboardingKey,
  testVrzOnboardingLink,
  VrzOnboardingLinkInfo,
} from '../../data/api';
import { APP_ENV } from '../config';

/* Show the banner unless this build is explicitly production. Failing open is
   the right direction: wrongly warning that a production page is staging is a
   moment's confusion, while wrongly presenting staging as production gets a
   real organisation created off test data. */
const IS_NON_PRODUCTION = APP_ENV !== 'production';

interface VirtualRoutezOnboardingScreenProps {
  token: string;
}

/** The partner-facing side of the Virtual Routez onboarding handshake.
 *
 * Reached only from the link in the "request onboarding" email -- there is no
 * AVCS login involved, on purpose: the person opening this is Virtual Routez
 * staff creating the organisation on their own platform, not an AVCS user.
 * They paste the API key they generated over there, then can test it
 * immediately so a copy-paste mistake shows up here rather than only being
 * discovered later by AVCS. */
export const VirtualRoutezOnboardingScreen: React.FC<VirtualRoutezOnboardingScreenProps> = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [linkError, setLinkError] = useState('');
  const [link, setLink] = useState<VrzOnboardingLinkInfo | null>(null);

  const [apiKey, setApiKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message?: string; closed?: boolean } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const info = await fetchVrzOnboardingLink(token);
        if (!cancelled) {
          setLink(info);
          setSubmitted(info.hasApiKey);
        }
      } catch (e: any) {
        if (!cancelled) setLinkError(e?.message || 'This link is invalid or has expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim() || submitting) return;
    setSubmitError('');
    setTestResult(null);
    setSubmitting(true);
    try {
      await submitVrzOnboardingKey(token, apiKey.trim());
      setSubmitted(true);
    } catch (e: any) {
      setSubmitError(e?.message || 'Could not save the key. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testVrzOnboardingLink(token);
      setTestResult({ ok: result.ok, message: result.message, closed: result.linkClosed });
    } catch (e: any) {
      setTestResult({ ok: false, message: e?.message || 'Could not test the key.' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <main className="card form-card" role="main">
        <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
        <p className="desc">Checking your link…</p>
      </main>
    );
  }

  if (linkError || !link) {
    return (
      <main className="card form-card" role="main" aria-labelledby="vrzOnboardingTitle">
        <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
        <span className="tag">Virtual Routez onboarding</span>
        <h1 id="vrzOnboardingTitle">This link no longer works</h1>
        <div className="alert error" role="alert">
          {linkError || 'This link is invalid or has expired.'}
        </div>
        <p className="desc">
          Ask AVCS to send a new onboarding request for this organisation.
        </p>
      </main>
    );
  }

  return (
    <main className="card form-card vrz-onboarding-card" role="main" aria-labelledby="vrzOnboardingTitle">
      {IS_NON_PRODUCTION && (
        <div className="env-banner" role="status">
          <span className="env-banner-badge">Staging</span>
          <span className="env-banner-text">
            This is the AVCS <strong>test</strong> system and the details below are <strong>test data</strong> —
            please create this organisation in the Virtual Routez <strong>acceptance</strong> environment
            (<code>accp.virtualroutez.app</code>), not production.
          </span>
        </div>
      )}

      {/* Branding left, the ask right: on the desktop this is read on, a
          centred stack pushed the entity name and the instruction most of a
          screen down for no gain. */}
      <div className="vrz-onboarding-header">
        <div className="vrz-onboarding-brand">
          <img src="/avcs-logo.png" alt="AVCS Logo" className="logo" />
          <span className="tag">Virtual Routez onboarding</span>
        </div>
        <div className="vrz-onboarding-intro">
          <h1 id="vrzOnboardingTitle">{link.entityName}</h1>
          <p className="desc">
            AVCS has created this {link.entityType.toLowerCase()} and needs it stood up on Virtual Routez. Please
            could you create the organisation from the matching template, using the details below, and then paste
            its API key at the bottom of this page once it has been generated. Thank you.
          </p>
        </div>
      </div>

      {/* Everything the partner needs to create the org, so they are not
          cross-referencing the request email while working in this page.
          Contact details are deliberately absent -- see
          describe_entity_for_onboarding on the backend.

          Two explicit columns rather than one auto-flowing grid: auto-flow
          fills left-to-right, so the GS1 codes would land in whichever cell
          happened to be next and split across columns as fields above them
          appear or disappear. Here who-and-where sits left, the codes sit
          together on the right. */}
      <div className="vrz-onboarding-details">
        <dl className="vrz-onboarding-col">
          <div><dt>Entity type</dt><dd>{link.entityType}</dd></div>
          {link.registeredBusinessName && (
            <div><dt>Registered business name</dt><dd>{link.registeredBusinessName}</dd></div>
          )}
          {link.farmName && <div><dt>Production unit</dt><dd>{link.farmName}</dd></div>}
          {link.entityNo && <div><dt>AVCS reference</dt><dd>{link.entityNo}</dd></div>}
          {link.addressLines.length > 0 && (
            <div>
              <dt>Address</dt>
              <dd>{link.addressLines.map((line) => <div key={line}>{line}</div>)}</dd>
            </div>
          )}
        </dl>

        <dl className="vrz-onboarding-col">
          {link.supplyChain && (
            <div><dt>Supply Chain Programme</dt><dd>{link.supplyChain}</dd></div>
          )}
          {link.latitude && link.longitude && (
            <div><dt>GPS</dt><dd><code>{link.latitude}, {link.longitude}</code></dd></div>
          )}
          {link.glns.length > 0 && (
            <div>
              <dt>{link.glns.length > 1 ? 'GLNs' : 'GLN'}</dt>
              <dd>{link.glns.map((g) => <div key={g}><code>{g}</code></div>)}</dd>
            </div>
          )}
          {link.gtins.length > 0 && (
            <div>
              <dt>{link.gtins.length > 1 ? 'GTINs' : 'GTIN'}</dt>
              <dd>
                {link.gtins.map((g) => (
                  <div key={g.code}>
                    <code>{g.code}</code>{g.productType ? ` — ${g.productType}` : ''}
                  </div>
                ))}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {link.missing.length > 0 && (
        <div className="alert warn" role="status" style={{ marginTop: '12px' }}>
          AVCS does not hold {link.missing.join(', ').toLowerCase()} for this {link.entityType.toLowerCase()}.
          Please ask AVCS rather than filling it in yourself.
        </div>
      )}

      <div aria-live="polite">
        {submitError && <div className="alert error" role="alert">{submitError}</div>}
        {submitted && !submitError && (
          <div className="alert success" role="status">
            Key saved to AVCS. Use "Test key" below to confirm it works, or paste a corrected key and save again.
          </div>
        )}
      </div>

      {!testResult?.closed && (
      <form onSubmit={handleSubmit} className="form" aria-label="Submit Virtual Routez API key">
        <label>
          <span>Virtual Routez API key</span>
          <input
            type="text"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Paste the API key generated on Virtual Routez"
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <div className="actions">
          <button className="btn primary" type="submit" disabled={!apiKey.trim() || submitting} aria-busy={submitting}>
            {submitting ? 'Saving…' : 'Save key'}
          </button>
        </div>
      </form>
      )}

      {submitted && !testResult?.closed && (
        <div className="actions" style={{ marginTop: '12px' }}>
          <button className="btn" type="button" onClick={handleTest} disabled={testing} aria-busy={testing}>
            {testing ? 'Testing…' : 'Test key'}
          </button>
        </div>
      )}

      {testResult && (
        <div className={`alert ${testResult.ok ? 'success' : 'error'}`} role="status" style={{ marginTop: '12px' }}>
          {testResult.ok
            ? 'The key works — AVCS can reach Virtual Routez with it.'
            : (testResult.message || 'Virtual Routez rejected this key.')}
        </div>
      )}

      {testResult?.closed ? (
        <p className="hint" style={{ marginTop: '16px' }}>
          Nothing further is needed — this link has now been closed, so it will stop working if you open it
          again. If the key ever needs replacing, ask AVCS to send a new onboarding request.
        </p>
      ) : (
        <p className="hint" style={{ marginTop: '16px' }}>
          This link expires {new Date(link.expiresAt).toLocaleDateString()}. If you need more time, ask AVCS to
          re-send the onboarding request.
        </p>
      )}
    </main>
  );
};
